/**
 * Logique de la page des interrupteurs globaux (D21) : l'état d'un module, le
 * résumé chiffré et le filtre. Pure, donc testée sans écran.
 */
import { MODULES_COEUR, MODULES_PILOTABLES, REGISTRE_MODULES } from '@artosera/shared';
import type { ModulePilotable } from '@artosera/shared';
import type { IdPalierDev, Interrupteurs } from '../types.js';

/**
 * `bientot` : pas livré ; `palier` : pas encore débloqué par son palier (neutre,
 * ce n'est pas une panne) ; `secours` : coupure d'environnement ; `coupe` :
 * coupure console.
 */
export type EtatModule = 'bientot' | 'palier' | 'secours' | 'coupe' | 'ouvert';

export type FiltreEtat = 'tous' | 'coupes' | 'verrouilles' | 'avenir';

/**
 * Le palier qui verrouille ce module, d'après le GET des interrupteurs
 * globaux, ou `null` : débloqué, cœur, ou API plus ancienne qui ne le dit pas.
 */
export function palierVerrouillant(module: string, donnees: Interrupteurs): IdPalierDev | null {
  const ligne = donnees.modules?.find((entree) => entree.module === module);
  return ligne && !ligne.debloque ? ligne.palier : null;
}

/**
 * La coupure d'urgence d'un module, quel que soit son palier : le palier et
 * l'urgence sont deux axes indépendants.
 */
export function coupeEnUrgence(
  module: ModulePilotable,
  donnees: Interrupteurs,
): 'secours' | 'coupe' | null {
  if (donnees.coupesSecours.includes(module)) return 'secours';
  if (donnees.coupes.some((ligne) => ligne.module === module)) return 'coupe';
  return null;
}

export function etatModule(module: ModulePilotable, donnees: Interrupteurs): EtatModule {
  if (!REGISTRE_MODULES[module].disponible) return 'bientot';
  if (palierVerrouillant(module, donnees) !== null) return 'palier';
  return coupeEnUrgence(module, donnees) ?? 'ouvert';
}

export interface Resume {
  pilotables: number;
  coupes: number;
  /** Modules que leur palier ne débloque pas encore ; `null` si l'API ne le dit pas. */
  nonDebloques: number | null;
  coeur: number;
}

/** Les modules coupés pour tout le parc : par la console ou par la variable de secours. */
export function resumer(donnees: Interrupteurs): Resume {
  const coupes = MODULES_PILOTABLES.filter((module) => coupeEnUrgence(module, donnees) !== null);
  return {
    pilotables: MODULES_PILOTABLES.length,
    coupes: coupes.length,
    nonDebloques: donnees.modules
      ? MODULES_PILOTABLES.filter((module) => palierVerrouillant(module, donnees) !== null).length
      : null,
    coeur: MODULES_COEUR.length,
  };
}

/** Minuscules, sans accents, ligatures dépliées : « Œuvres » se trouve en tapant « oeuvres ». */
export function normaliser(texte: string): string {
  return texte
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
}

export function correspond(
  module: ModulePilotable,
  donnees: Interrupteurs,
  filtre: FiltreEtat,
  recherche: string,
  textes: { libelle: string; description: string },
): boolean {
  const etat = etatModule(module, donnees);
  if (filtre === 'coupes' && coupeEnUrgence(module, donnees) === null) return false;
  if (filtre === 'verrouilles' && etat !== 'palier') return false;
  if (filtre === 'avenir' && etat !== 'bientot') return false;
  const requete = normaliser(recherche);
  if (requete === '') return true;
  return normaliser(`${textes.libelle} ${textes.description}`).includes(requete);
}
